import React, { useState, useEffect } from 'react';
import './TopWidgetEdit.css';
import { useWidgetEdit } from '../hooks/useWidgetEdit';
import ImageContainer from '../WidgetEditComponents/ImageContainer';
import TextSettingsSection from '../WidgetEditComponents/TextSettingsSection';
import RichTextEditor from '../WidgetEditComponents/RichTextEditor';
import MoreSettingsSection from '../WidgetEditComponents/MoreSettingsSection';
import AdvancedSettingsSection from '../WidgetEditComponents/AdvancedSettingsSection';
import ColorPickerSection from '../WidgetEditComponents/ColorPickerSection';
import ToggleSwitch from '../WidgetEditComponents/ToggleSwitch';
import IconPicker from '../IconPicker';
import RevolutionaryLayoutSettings from '../WidgetEditComponents/RevolutionaryLayoutSettings';
import LayoutSelector2025 from '../../components/LayoutSelector2025';
import SaveTemplatePopup from '../WidgetEditComponents/SaveTemplatePopup';

function SpiritualQuoteCardEdit({ widget, onUpdate, colors }) {
    const { editData, handleInputChange: originalHandleInputChange, uploadImage, handleSliderChange } = useWidgetEdit(widget, onUpdate);
    
    // ✅ RACE CONDITION PREVENTION - KRITISCH für Widget-Switches
    const [isInitialized, setIsInitialized] = useState(false);
    const [previousLayoutType, setPreviousLayoutType] = useState(editData.layoutType);
    
    // 🔧 FIREBASE-SAFE INPUT HANDLER - Verhindert undefined-Werte
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        let safeValue;
        
        if (type === 'checkbox') {
            safeValue = Boolean(checked); // Garantiert true/false, nie undefined
        } else if (value === undefined || value === null) {
            safeValue = ''; // Leerer String statt undefined/null
        } else {
            safeValue = value;
        }
        
        // Erstelle sicheren Event mit garantiert definierten Werten
        const safeEvent = {
            target: {
                name,
                value: safeValue,
                type,
                checked: type === 'checkbox' ? safeValue : checked
            }
        };
        
        console.log(`🔧 [SAFE-INPUT] ${name}:`, safeValue, `(type: ${type})`);
        originalHandleInputChange(safeEvent);
    };
    
    // Image Upload States
    const [isUploadingBackground, setIsUploadingBackground] = useState(false);
    const [isUploadingIcon, setIsUploadingIcon] = useState(false);
    
    // Icon Picker State
    const [showIconPicker, setShowIconPicker] = useState(false);
    
    // ✅ SAVE TEMPLATE POPUP STATE
    const [showSaveTemplatePopup, setShowSaveTemplatePopup] = useState(false);

    // ✅ INITIALIZATION EFFECT - Set initialized flag after first render
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsInitialized(true);
            console.log('🚀 SpiritualQuoteCard initialized - layout variations now active');
        }, 100);
        
        return () => clearTimeout(timer);
    }, []);

    // ✅ LAYOUT VARIATIONS SYSTEM mit Race Condition Prevention
    useEffect(() => {
        const currentLayout = editData.layoutType;
        
        // ✅ CRITICAL: Only apply layout variations on ACTUAL layout changes, not widget switches
        if (!isInitialized) {
            console.log('🚫 SpiritualQuoteCard: Skipping layout variations - not initialized yet');
            return;
        }
        
        if (previousLayoutType === currentLayout) {
            console.log('🚫 SpiritualQuoteCard: Skipping layout variations - same layout type');
            return;
        }
        
        console.log(`🚀 SpiritualQuoteCard REAL layout change detected: ${previousLayoutType} → ${currentLayout}`);
        setPreviousLayoutType(currentLayout);
        
        // 🎯 LAYOUT-SPEZIFISCHE DEFAULTS
        const layoutVariations = {
            'modern-flat': {
                height: 'auto',                 // Desktop Height - Auto für Content-Anpassung
                heightMobile: 'auto',           // Mobile Height - Auto für Content-Anpassung
                containerPadding: 48,           // Desktop Container Padding
                containerPaddingMobile: 32,     // Mobile Container Padding
                iconTitleSpacing: 20,           // Desktop Icon-Title Spacing
                iconTitleSpacingMobile: 16,     // Mobile Icon-Title Spacing
                titleSymbolSpacing: 16,         // Desktop Title-Symbol Spacing
                titleSymbolSpacingMobile: 12,   // Mobile Title-Symbol Spacing
                symbolTextSpacing: 24,          // Desktop Symbol-Text Spacing
                symbolTextSpacingMobile: 20,    // Mobile Symbol-Text Spacing
                textAlignment: 'center',        // Desktop Text Alignment
                textAlignmentMobile: 'center',  // Mobile Text Alignment
                textMarginX: 0,                 // Desktop Text Margin
                textMarginXMobile: 0,           // Mobile Text Margin
                topIconSize: 48,                // Standard Icon Size
                topIconInvert: false,           // Icon nicht invertiert
                showTitleText: true,
                showRichText: true,
                showTopIcon: true,
                showDecorativeSymbol: true
            },
            'elegant-minimal': {
                height: 'auto',                 // Desktop Height - Auto für Content-Anpassung
                heightMobile: 'auto',           // Mobile Height - Auto für Content-Anpassung
                containerPadding: 64,           // Desktop Container Padding - großzügiger
                containerPaddingMobile: 40,     // Mobile Container Padding - elegant
                iconTitleSpacing: 24,           // Desktop Icon-Title Spacing - mehr Raum
                iconTitleSpacingMobile: 20,     // Mobile Icon-Title Spacing
                titleSymbolSpacing: 20,         // Desktop Title-Symbol Spacing - eleganter
                titleSymbolSpacingMobile: 16,   // Mobile Title-Symbol Spacing
                symbolTextSpacing: 28,          // Desktop Symbol-Text Spacing - mehr Raum
                symbolTextSpacingMobile: 24,    // Mobile Symbol-Text Spacing
                textAlignment: 'center',        // Desktop Text Alignment
                textAlignmentMobile: 'center',  // Mobile Text Alignment
                textMarginX: 20,                // Desktop Text Margin - elegante Ränder
                textMarginXMobile: 16,          // Mobile Text Margin
                topIconSize: 52,                // Etwas größeres Icon für Eleganz
                topIconInvert: false,           // Icon nicht invertiert
                decorativeSymbolSize: 18,       // Größeres Symbol
                showTitleText: true,
                showRichText: true,
                showTopIcon: true,
                showDecorativeSymbol: true
            },
            'luxury-minimal': {
                height: 500,                    // Desktop Height - premium Höhe
                heightMobile: 1400,             // Mobile Height - luxuriöser Raum
                containerPadding: 80,           // Desktop Container Padding - premium
                containerPaddingMobile: 48,     // Mobile Container Padding - luxuriös
                iconTitleSpacing: 32,           // Desktop Icon-Title Spacing - großzügig
                iconTitleSpacingMobile: 24,     // Mobile Icon-Title Spacing
                titleSymbolSpacing: 24,         // Desktop Title-Symbol Spacing - premium
                titleSymbolSpacingMobile: 20,   // Mobile Title-Symbol Spacing
                symbolTextSpacing: 36,          // Desktop Symbol-Text Spacing - luxuriös
                symbolTextSpacingMobile: 28,    // Mobile Symbol-Text Spacing
                textAlignment: 'center',        // Desktop Text Alignment
                textAlignmentMobile: 'center',  // Mobile Text Alignment
                textMarginX: 40,                // Desktop Text Margin - luxuriöse Ränder
                textMarginXMobile: 24,          // Mobile Text Margin
                topIconSize: 56,                // Größeres Icon für Luxury-Look
                topIconInvert: false,           // Icon nicht invertiert
                decorativeSymbolSize: 20,       // Größeres Symbol für Premium-Feel
                showTitleText: true,
                showRichText: true,
                showTopIcon: true,
                showDecorativeSymbol: true
            }
        };
        
        const variations = layoutVariations[currentLayout];
        if (variations) {
            console.log(`🎯 Applying layout variations for ${currentLayout}:`, variations);
            
            // ✅ KRITISCH: BATCH UPDATE - Alle Werte auf einmal setzen
            const safePosition = widget.position !== undefined && widget.position !== null ? widget.position : 0;
            const updatedEditData = { ...editData, ...variations };
            
            // ✅ FIREBASE-SAFE: Position immer mitgesendet
            onUpdate({ ...updatedEditData, position: safePosition });
            
            console.log(`✅ SpiritualQuoteCard layout variations applied via batch update`);
        }
    }, [editData.layoutType, isInitialized, previousLayoutType, editData, onUpdate, widget.position]);

    // ✅ LAYOUT CHANGE HANDLER
    const handleLayoutChange = (layoutId, layoutConfig) => {
        console.log(`🎨 SpiritualQuoteCard layout change: ${layoutId}`, layoutConfig);
        const fakeEvent = {
            target: { name: 'layoutType', value: layoutId, type: 'text' }
        };
        handleInputChange(fakeEvent);
    };

    // Icon Selection Handler
    const handleIconSelect = (icon) => {
        const iconSrc = icon?.src || icon; // Handle both icon object and direct src
        const fakeEvent = {
            target: {
                name: 'topIcon',
                value: iconSrc,
                type: 'text'
            }
        };
        handleInputChange(fakeEvent);
        setShowIconPicker(false);
    };

    // Decorative Symbol Change Handler
    const handleSymbolChange = (symbol) => {
        const fakeEvent = {
            target: {
                name: 'decorativeSymbol',
                value: symbol,
                type: 'text'
            }
        };
        handleInputChange(fakeEvent);
    };

    // Container Style ist jetzt fest auf 'modern-flat' gesetzt

    // 🚀 REVOLUTIONARY TEXT SETTINGS - Alle Text-Felder mit Show/Hide Toggles
    const textConfig = [
        {
            fieldName: 'titleText',
            label: 'Card Title',
            type: 'input',
            placeholder: 'Your Inner Wisdom',
            fontField: 'titleFont',
            fontType: 'big',
            effectField: 'titleEffect'
        },
        {
            fieldName: 'richText',
            label: 'Quote Content (Rich Text)',
            type: 'textarea',
            placeholder: 'Enter your spiritual quote or wisdom here...',
            rows: 4,
            fontField: 'richTextFont',
            fontType: 'small',
            effectField: 'richTextEffect'
        }
    ];

    // Color Configuration
    const colorConfig = [
        {
            label: 'Background Color',
            fieldName: 'backgroundColor',
            value: editData.backgroundColor
        },
        {
            label: 'Container Background',
            fieldName: 'containerBackground',
            value: editData.containerBackground
        },
        {
            label: 'Title Color',
            fieldName: 'titleColor',
            value: editData.titleColor
        },
        {
            label: 'Rich Text Color',
            fieldName: 'richTextColor',
            value: editData.richTextColor
        },
        {
            label: 'Icon Color',
            fieldName: 'topIconColor',
            value: editData.topIconColor
        },
        {
            label: 'Symbol Color',
            fieldName: 'decorativeSymbolColor',
            value: editData.decorativeSymbolColor
        }
    ];

    // Font Configuration für MoreSettingsSection
    const fontConfig = [
        {
            label: 'Title',
            sizeField: 'titleSize',
            weightField: 'titleWeight',
            defaultSize: '32',
            defaultWeight: '600'
        },
        {
            label: 'Rich Text',
            sizeField: 'richTextSize',
            weightField: 'richTextWeight',
            defaultSize: '18',
            defaultWeight: '400'
        }
    ];

    // 🚀 REVOLUTIONARY LAYOUT SETTINGS - Custom Properties mit Mobile/Desktop Support
    const customLayoutProperties = [
        {
            section: 'Container Settings',
            icon: 'fa-window-maximize',
            properties: [
                {
                    id: 'containerPadding',
                    label: 'Container Padding',
                    min: 20,
                    max: 80,
                    step: 4,
                    unit: 'px',
                    icon: '📦',
                    description: 'Inner padding of the quote container',
                    defaultValue: 48
                }
            ]
        },
        {
            section: 'Spacing Settings',
            icon: 'fa-arrows-v',
            properties: [
                {
                    id: 'iconTitleSpacing',
                    label: 'Icon-Title Spacing',
                    min: 10,
                    max: 50,
                    step: 2,
                    unit: 'px',
                    icon: '↕️',
                    description: 'Space between icon and title',
                    defaultValue: 20
                },
                {
                    id: 'titleSymbolSpacing',
                    label: 'Title-Symbol Spacing',
                    min: 8,
                    max: 30,
                    step: 2,
                    unit: 'px',
                    icon: '↕️',
                    description: 'Space between title and decorative symbol',
                    defaultValue: 16
                },
                {
                    id: 'symbolTextSpacing',
                    label: 'Symbol-Text Spacing',
                    min: 16,
                    max: 40,
                    step: 2,
                    unit: 'px',
                    icon: '↕️',
                    description: 'Space between symbol and rich text',
                    defaultValue: 24
                }
            ]
        },
        {
            section: 'Text Layout Settings',
            icon: 'fa-align-center',
            properties: [
                {
                    id: 'textMarginX',
                    label: 'Text Margin (Horizontal)',
                    min: 0,
                    max: 200,
                    step: 8,
                    unit: 'px',
                    icon: '↔️',
                    description: 'Horizontal margin from container edges to text',
                    defaultValue: 0
                }
            ]
        },
        {
            section: 'Icon & Symbol Settings',
            icon: 'fa-star',
            properties: [
                {
                    id: 'topIconSize',
                    label: 'Icon Size',
                    min: 24,
                    max: 400,
                    step: 4,
                    unit: 'px',
                    icon: '🌟',
                    description: 'Size of the top icon (up to 400px for large images)',
                    defaultValue: 48
                },
                {
                    id: 'decorativeSymbolSize',
                    label: 'Symbol Size',
                    min: 12,
                    max: 32,
                    step: 1,
                    unit: 'px',
                    icon: '✨',
                    description: 'Size of decorative symbols',
                    defaultValue: 16
                }
            ]
        }
    ];

    // 🔧 ADVANCED SETTINGS CONFIGURATION - Nur noch Widget-spezifische Properties
    const advancedConfig = [

        // Behavior Group
        {
            fieldName: 'showTopIcon',
            label: 'Show Top Icon',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/background.svg'
        },
        {
            fieldName: 'topIconInvert',
            label: 'Invert Top Icon',
            type: 'toggle',
            defaultValue: false,
            icon: '/img/background.svg',
            description: 'Invert icon colors (useful for dark backgrounds or when using images)'
        },
        {
            fieldName: 'showDecorativeSymbol',
            label: 'Show Decorative Symbol',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/text.svg'
        },

        // Text Alignment Group
        {
            fieldName: 'textAlignment',
            label: 'Text Alignment',
            type: 'select',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' }
            ],
            defaultValue: 'center',
            icon: '/img/down.svg',
            description: 'Horizontal alignment of all text content'
        },
        {
            fieldName: 'textAlignmentMobile',
            label: 'Mobile Text Alignment',
            type: 'select',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' }
            ],
            defaultValue: 'center',
            icon: '/img/down.svg',
            description: 'Text alignment on mobile devices'
        },

        // Decorative Symbols Group
        {
            fieldName: 'decorativeSymbol',
            label: 'Decorative Symbol',
            type: 'select',
            defaultValue: '✦',
            options: [
                { value: '✦', label: '✦ Star 1' },
                { value: '✧', label: '✧ Star 2' },
                { value: '✩', label: '✩ Star 3' },
                { value: '✪', label: '✪ Star 4' },
                { value: '✫', label: '✫ Star 5' },
                { value: '✬', label: '✬ Star 6' },
                { value: '✭', label: '✭ Star 7' },
                { value: '✮', label: '✮ Star 8' },
                { value: '✯', label: '✯ Star 9' },
                { value: '✰', label: '✰ Star 10' },
                { value: '✱', label: '✱ Star 11' },
                { value: '✲', label: '✲ Star 12' },
                { value: '✳', label: '✳ Star 13' },
                { value: '✴', label: '✴ Star 14' },
                { value: '✵', label: '✵ Star 15' },
                { value: '✶', label: '✶ Star 16' },
                { value: '✷', label: '✷ Star 17' },
                { value: '✸', label: '✸ Star 18' },
                { value: '✹', label: '✹ Star 19' },
                { value: '✺', label: '✺ Star 20' },
                { value: '✻', label: '✻ Star 21' },
                { value: '✼', label: '✼ Star 22' },
                { value: '✽', label: '✽ Star 23' },
                { value: '✾', label: '✾ Star 24' }
            ],
            icon: '/img/text.svg'
        },

        // Entfernte Regler (jetzt über andere Sektionen verwaltet):
        // - containerRadius: Wird über RevolutionaryLayoutSettings verwaltet
        // - borderRadius: Wird über RevolutionaryLayoutSettings verwaltet  
        // - titleSize: Wird über TextSettingsSection verwaltet
        // - backgroundImageOpacity: Wird über ImageContainer Controls verwaltet
        // - topIconSize: Wird über RevolutionaryLayoutSettings verwaltet
        // - decorativeSymbolSize: Wird über RevolutionaryLayoutSettings verwaltet
    ];


    return (
        <div className="widget-edit-container">
            {/* ✅ LAYOUT SELECTOR 2025 */}
            <LayoutSelector2025
                widgetType="spiritualQuoteCard"
                selectedLayout={editData.layoutType || 'modern-flat'}
                onLayoutChange={handleLayoutChange}
            />

            {/* 1. BACKGROUND IMAGE CONTAINER */}
            <ImageContainer
                label="Background Image"
                fieldName="backgroundImage"
                currentImage={editData.backgroundImage}
                onUpload={uploadImage}
                isUploading={isUploadingBackground}
                setIsUploading={setIsUploadingBackground}
                controls={[
                    {
                        type: 'size',
                        label: 'Image Opacity',
                        fieldName: 'backgroundImageOpacity',
                        defaultValue: 20,
                        min: 5,
                        max: 100,
                        step: 5,
                        unit: '%',
                        description: 'Opacity of the background image',
                        convertValue: true
                    },
                    {
                        type: 'image-effect',
                        label: 'Image Effect',
                        fieldName: 'imageEffect',
                        defaultValue: 'none'
                    }
                ]}
                editData={editData}
                handleInputChange={handleInputChange}
                theme="default"
            />

            {/* 2. TOP ICON CONTAINER - mit Icon Picker Integration */}
            <ImageContainer
                label="Top Icon"
                fieldName="topIcon"
                currentImage={editData.topIcon}
                onUpload={uploadImage}
                isUploading={isUploadingIcon}
                setIsUploading={setIsUploadingIcon}
                showToggle={true}
                toggleLabel="Show Top Icon"
                toggleFieldName="showTopIcon"
                toggleChecked={editData.showTopIcon !== false}
                onToggleChange={handleInputChange}
                controls={[
                    {
                        type: 'size',
                        label: 'Icon Size',
                        fieldName: 'topIconSize',
                        defaultValue: 48,
                        min: 24,
                        max: 80,
                        step: 2,
                        unit: 'px',
                        description: 'Size of the top icon'
                    }
                ]}
                editData={editData}
                handleInputChange={handleInputChange}
                theme="default"
                compact={true}
            />

            {/* Icon Picker Integration */}
            {editData.showTopIcon !== false && (
                <div className="widget-edit-section">
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '16px'
                    }}>
                        <button 
                            className="icon-picker-button"
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowIconPicker(!showIconPicker);
                            }}
                            style={{
                                width: '60px',
                                height: '60px',
                                border: '2px dashed #d1d5db',
                                borderRadius: '12px',
                                background: editData.topIcon ? '#f9fafb' : 'transparent',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            {editData.topIcon ? (
                                <img 
                                    src={editData.topIcon} 
                                    alt="Top Icon" 
                                    style={{ width: '36px', height: '36px', objectFit: 'contain' }} 
                                />
                            ) : (
                                <span style={{ fontSize: '14px', color: '#9ca3af' }}>+</span>
                            )}
                        </button>
                        
                        <div style={{ textAlign: 'left' }}>
                            <div style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
                                Choose from Icon Library
                            </div>
                            <div style={{ fontSize: '10px', color: '#6b7280' }}>
                                Click to select spiritual icons
                            </div>
                        </div>
                    </div>

                    {showIconPicker && (
                        <div style={{
                            padding: '16px',
                            background: '#ffffff',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                            maxHeight: '300px',
                            overflow: 'hidden',
                            marginBottom: '16px'
                        }}>
                            <IconPicker
                                onSelect={handleIconSelect}
                                onClose={() => setShowIconPicker(false)}
                                defaultFilter="Handdrawn"
                            />
                        </div>
                    )}
                </div>
            )}

            {/* 3. TEXT SETTINGS - Revolutionäres Text-System */}
            <TextSettingsSection 
                textConfig={textConfig}
                editData={editData}
                handleInputChange={handleInputChange}
                showFontSelector={true}
                showTextEffects={true}
                showButtonEffects={false}
            />

            {/* 🚀 REVOLUTIONARY LAYOUT SETTINGS - Standard + Custom Properties */}
            <RevolutionaryLayoutSettings 
                editData={editData}
                handleInputChange={handleInputChange}
                customProperties={customLayoutProperties}
            />

            {/* 4. ADVANCED SETTINGS - Nur noch Widget-spezifische Properties */}
            <AdvancedSettingsSection 
                advancedConfig={advancedConfig}
                editData={editData}
                handleInputChange={handleInputChange}
                handleSliderChange={handleSliderChange}
                defaultExpanded={false}
            />

            {/* 5. MORE SETTINGS - Colors & Fonts only */}
            <MoreSettingsSection
                colors={colors}
                colorConfig={colorConfig}
                fontConfig={fontConfig}
                layoutConfig={[]} // Empty - moved to Revolutionary Layout Settings
                editData={editData}
                handleInputChange={handleInputChange}
            />

            {/* 💾 SAVE AS TEMPLATE BUTTON */}
            <div className="save-template-section">
                <button 
                    className="save-template-btn"
                    onClick={() => setShowSaveTemplatePopup(true)}
                    title="Save this widget configuration as a reusable template"
                >
                    <i className="fas fa-save"></i>
                    Save as Template
                </button>
            </div>

            {/* 💾 SAVE TEMPLATE POPUP */}
            <SaveTemplatePopup
                isOpen={showSaveTemplatePopup}
                onClose={() => setShowSaveTemplatePopup(false)}
                widgetData={editData}
                originalWidgetType={widget.type}
            />
        </div>
    );
}

export default SpiritualQuoteCardEdit;
